const NO_FOCUSED_DIFFERENCE_ID = '-1';

export interface DifferenceBoxAnnotation {
  id: string;
  groupId?: string;
  hidden: boolean;
  /** True when this box was hidden by Hide other differences, not by a filter or placeholder. */
  hiddenByHideOthers: boolean;
  /** True when the Re-Inspect tool hid this box because its type is not eligible for resync. */
  hiddenByResync: boolean;
  isDifference: boolean;
  isDeletionMarker: boolean;
}

/**
 * Whether this difference box should be hidden while another difference is selected.
 *
 * A grouped result is one selection: the list card and the dimming overlay show every member
 * of that group, so those boxes stay visible. Every difference outside that selection is hidden.
 */
export function shouldHideUnselectedDifferenceBox(visibility: {
  hideOtherDifferences: boolean;
  focusedDifferenceId: string | undefined;
  focusedGroupId?: string;
  annotationId: string;
  annotationGroupId?: string;
}): boolean {
  if (!visibility.hideOtherDifferences) return false;

  const focusedDifferenceId = visibility.focusedDifferenceId;
  if (!focusedDifferenceId || focusedDifferenceId === NO_FOCUSED_DIFFERENCE_ID) return false;
  if (visibility.annotationId === focusedDifferenceId) return false;

  const { annotationGroupId, focusedGroupId } = visibility;
  if (annotationGroupId && focusedGroupId && annotationGroupId === focusedGroupId) return false;

  return true;
}

/**
 * Which difference boxes to hide or show. Deletion and insertion markers are left to their own
 * visibility logic. Filtered-out and placeholder boxes stay hidden: we only reveal a box we hid
 * ourselves, and only if the difference is currently displayed. Boxes the Re-Inspect tool hid are
 * left for it to restore, since it may have tagged them while they were already hidden by us.
 */
export function planDifferenceBoxVisibility(input: {
  hideOtherDifferences: boolean;
  focusedDifferenceId: string | undefined;
  focusedGroupId?: string;
  displayedDifferenceIds: ReadonlySet<string>;
  annotations: DifferenceBoxAnnotation[];
}) {
  const hideIds: string[] = [];
  const showIds: string[] = [];
  const clearFlagIds: string[] = [];

  input.annotations.forEach((annotation) => {
    if (!annotation.isDifference || annotation.isDeletionMarker) return;

    const shouldHide = shouldHideUnselectedDifferenceBox({
      hideOtherDifferences: input.hideOtherDifferences,
      focusedDifferenceId: input.focusedDifferenceId,
      focusedGroupId: input.focusedGroupId,
      annotationId: annotation.id,
      annotationGroupId: annotation.groupId,
    });

    if (shouldHide) {
      if (!annotation.hidden) hideIds.push(annotation.id);
      return;
    }

    if (!annotation.hiddenByHideOthers) return;

    clearFlagIds.push(annotation.id);
    if (input.displayedDifferenceIds.has(annotation.id) && !annotation.hiddenByResync) showIds.push(annotation.id);
  });

  return { hideIds, showIds, clearFlagIds };
}
